import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import type {
  ChatLengthLimit,
  ChatSendCapability,
  ChatSendConnectionTargets,
  ChatSendResults,
  ChatSendTargetResult,
} from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../components/Alert.js';
import { PlatformIcon } from '../components/PlatformIcon.js';

/**
 * The box an operator types into (Connaro Chat, ARCHITECTURE.md §22).
 *
 * ======================= UNKNOWN IS NOT SUCCESS =======================
 * `POST /chat/send` answers a RESULT PER TARGET, and each one is SENT, FAILED,
 * or — the one that matters — UNKNOWN, which means Connaro passed the message
 * to the worker and cannot confirm what became of it. The HTTP code for a
 * fan-out containing an UNKNOWN is 202, which satisfies `response.ok`.
 * Anything here that branched on the status code would call that a success,
 * the operator would not check, and either the audience never saw the message
 * or they saw it twice when it was sent again.
 *
 * So every branch below reads the `status` FIELD of each result, and UNKNOWN
 * gets its own words: it is not "Sent", and it is not "Not sent". The text
 * stays in the box unless EVERY target reported SENT — not as a convenience,
 * but because clearing it would suggest the message is gone and settled when
 * one platform's outcome is still open.
 * ======================================================================
 *
 * ======================= CHOOSING WHERE IT GOES =======================
 * Every platform that can send is a CHECKBOX, all of them visible at once.
 * There is no "all platforms" option and there deliberately is not one: a
 * single label standing for a set is a label an operator has to trust, and
 * what it stands for changes silently when a connection is added or drops out
 * of SUPPORTED. Ticked boxes are the set — nothing is hidden behind a word,
 * and the state is on screen while the message is being typed rather than
 * folded into a closed dropdown.
 *
 * Whatever is ticked produces ONE request carrying several targets — never a
 * loop of requests from here. The server refuses the whole message if any one
 * platform would reject it, which is the only way a message too long for
 * Twitch does not reach the Discord half of the audience on its own. See the
 * `targets` comment on `sendBodySchema` in `apps/api/src/routes/chat.ts` for
 * the two other reasons a browser-side fan-out is not equivalent.
 *
 * The consequence here is that the outcome is a LIST, and it is rendered as
 * one. There is no single word summarising a mixed result, because any such
 * word is wrong about at least one platform.
 * ======================================================================
 *
 * WHAT THIS COMPONENT DOES NOT DECIDE. Whether a connection may send, why it
 * may not, whether a channel must be chosen, and how long a message may be are
 * all answered by the server, per connection, and rendered as given:
 *
 *   `GET /chat/capabilities`  state + the adapter's own note + the limits
 *   `GET /chat/channels`      requiresChannel + the channels a send accepts,
 *                             for every connection in the workspace
 *
 * Two of those are rules the send route ENFORCES. A copy of either in the
 * browser would be free to drift from the copy that decides — and because the
 * send route REFUSES a `channelId` for a non-Discord platform rather than
 * ignoring it, a drifted Console would start making requests the server
 * rejects. There is no `platform === 'discord'` test in this file, deliberately.
 *
 * §21, §57: the operator's text and every reason returned about it are
 * UNTRUSTED and rendered through React text nodes. Nothing here goes near
 * `dangerouslySetInnerHTML`, on a page that also renders arbitrary viewer input.
 */

/**
 * Counting, which is not the same thing as a limit.
 *
 * The NUMBERS a message is counted against come from the server (`limit`
 * below, straight from the API's `CHAT_LIMITS`) — the Console holds no
 * per-platform figures of its own, because a figure about somebody else's
 * product is a claim, and a claim compiled into a bundle is one nothing keeps
 * current.
 *
 * What happens here is measurement: how many user-perceived characters, and
 * how many UTF-8 bytes, this string is. `Intl.Segmenter` rather than
 * `String.length`, which counts UTF-16 code units and gets multi-code-point
 * emoji wrong in both directions — a family emoji is ONE character to a
 * platform and eleven to `String.length`. Hoisted to module scope because this
 * runs on every keystroke.
 *
 * The count is a courtesy. `validateChatMessage` on the server is the
 * guarantee, and it runs on every send, once per target, regardless of what
 * this said.
 */
const GRAPHEME_SEGMENTER = new Intl.Segmenter('en', { granularity: 'grapheme' });
const UTF8 = new TextEncoder();

type Measurement = { graphemes: number; utf8Bytes: number };

function measure(text: string): Measurement {
  return {
    graphemes: [...GRAPHEME_SEGMENTER.segment(text)].length,
    utf8Bytes: UTF8.encode(text).length,
  };
}

/**
 * The over-limit sentence, or null.
 *
 * Both of Kick's axes are checked because either can refuse a message the
 * other allows — 500 grapheme clusters AND 2048 UTF-8 bytes, independently.
 * Emoji spend bytes far faster than characters, so a message can sit at a
 * quarter of the character count and still be too long.
 */
function tooLong(
  displayName: string,
  limit: ChatLengthLimit | null,
  measured: Measurement,
): string | null {
  if (limit === null) return null;

  if (measured.graphemes > limit.graphemes) {
    return `Message too long for ${displayName}: ${String(measured.graphemes)} of ${String(limit.graphemes)} characters.`;
  }

  if (limit.utf8Bytes !== undefined && measured.utf8Bytes > limit.utf8Bytes) {
    return `Message too long for ${displayName}: ${String(measured.utf8Bytes)} of ${String(limit.utf8Bytes)} bytes. Emoji and other multi-byte characters count for more than one byte each.`;
  }

  return null;
}

/**
 * THE FIRST platform that would refuse this message, not a summary of all of
 * them.
 *
 * The server refuses the whole send if ANY target would reject it, so one
 * sentence naming one platform is the complete answer to "why can I not press
 * Send" — a list of every platform that is also over would be more words for
 * the same single fix. Order follows the picker, so the sentence is stable
 * between keystrokes rather than jumping between platforms.
 */
function firstRefusal(
  chosen: readonly ChatSendCapability[],
  measured: Measurement,
): string | null {
  for (const entry of chosen) {
    const reason = tooLong(entry.displayName, entry.limit, measured);
    if (reason !== null) return reason;
  }
  return null;
}

/**
 * The SHORTEST limit among the platforms selected, which is the one that
 * actually binds.
 *
 * Showing Discord's 2000 while Twitch's 500 is what refuses the message would
 * be a counter that says "you have room" right up to the failure. Where the
 * chosen platforms disagree, `shortest` names the one setting the floor, and
 * the composer says so — an unexplained 500 beside a Discord connection is its
 * own small confusion.
 *
 * A `null` limit is skipped rather than treated as zero. Every SUPPORTED
 * platform has a limit (the cross-table guard in `capability-honesty.test.ts`
 * makes that a build failure otherwise), so this is defensive only, and the
 * server's refusal is what would report it.
 */
function bindingLimit(chosen: readonly ChatSendCapability[]): {
  limit: ChatLengthLimit | null;
  shortest: string | null;
} {
  const declared = chosen.filter(
    (entry): entry is ChatSendCapability & { limit: ChatLengthLimit } => entry.limit !== null,
  );

  const first = declared[0];
  if (first === undefined) return { limit: null, shortest: null };

  const graphemes = Math.min(...declared.map((entry) => entry.limit.graphemes));

  /*
   * The two axes are minimised INDEPENDENTLY: the platform with the shorter
   * character cap is not necessarily the one with the shorter byte cap, and a
   * message has to satisfy both wherever both are declared. Platforms that
   * declare no byte cap simply do not contribute one.
   */
  const bytes = declared
    .map((entry) => entry.limit.utf8Bytes)
    .filter((value): value is number => value !== undefined);

  // The platform actually setting the floor, named only when they disagree.
  const tightest = declared.find((entry) => entry.limit.graphemes === graphemes) ?? first;
  const disagree = declared.some((entry) => entry.limit.graphemes !== graphemes);

  return {
    limit: { graphemes, ...(bytes.length === 0 ? {} : { utf8Bytes: Math.min(...bytes) }) },
    shortest: disagree ? tightest.displayName : null,
  };
}

/*
 * There is no `unavailableLead` here, and there was.
 *
 * It composed "Connaro cannot send chat messages on X." from the state name —
 * a sentence about what the SERVER will refuse, authored in a browser, keyed
 * off a state — and it drifted immediately: it added "yet" for
 * NOT_IMPLEMENTED, which the server's own refusal does not say. The lead now
 * arrives as a field, written by the same function the refusal uses
 * (`unavailableLead` in `apps/api/src/routes/chat.ts`), and an integration
 * test pins `lead + note` to the message a real refusal returns.
 */

/**
 * What came back, in three shapes that must not be collapsed into each other.
 *
 *   results      the server dispatched, and answered per target
 *   refused      the server refused BEFORE dispatching anything. Nothing was
 *                sent, on any platform, and that is a definite claim
 *   unreachable  the request may or may not have arrived. Every target is
 *                therefore unknown, and this side cannot narrow it
 *
 * `refused` and `unreachable` are deliberately not folded into a synthetic
 * list of per-target results. A row saying "Twitch: not sent" would be a claim
 * about Twitch that this side cannot make when the request never got a reply —
 * and inventing rows to make one rendering path work is how a "failed" gets
 * attached to a message that actually went out.
 */
type Outcome =
  | { kind: 'results'; results: ChatSendTargetResult[] }
  | { kind: 'refused'; reason: string }
  | { kind: 'unreachable'; reason: string };

/**
 * Send, and report what actually happened.
 *
 * `apiRequest` is deliberately NOT used. It throws on any non-2xx, which would
 * turn the 502 and 207 bodies — the ones carrying the operator-facing reasons
 * the server took care to produce — into a generic "Something went wrong".
 * Both shapes are read here instead:
 *
 *   a RESULTS body     `{ results: [...] }`, on 200, 202, 207 and 502
 *   an ERROR envelope  `{ error: { code, message } }`, on 400/403/404/409/429
 *
 * A refusal is a definite "not sent": nothing was dispatched, and the message
 * is the server's own. A transport failure is the honest unknown — the request
 * may have arrived and been acted on before the connection dropped, and this
 * side cannot tell.
 */
async function postChatSend(body: {
  text: string;
  targets: { platformConnectionId: string; channelId?: string }[];
}): Promise<Outcome> {
  let response: Response;
  try {
    response = await fetch('/api/v1/chat/send', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return {
      kind: 'unreachable',
      reason: 'Connaro could not be reached, so it does not know whether the message went out.',
    };
  }

  const payload: unknown = await response.json().catch(() => undefined);
  const parsed = payload as
    | (Partial<ChatSendResults> & { error?: { message?: string } })
    | undefined;

  if (Array.isArray(parsed?.results)) {
    return { kind: 'results', results: parsed.results };
  }

  if (!response.ok) {
    return {
      kind: 'refused',
      reason: parsed?.error?.message ?? 'Connaro refused the message and gave no reason.',
    };
  }

  /*
   * A 2xx that carries no results. Reported as unknown rather than assumed to
   * be a success: an answer this side does not understand is exactly the case
   * where guessing "sent" causes the duplicate.
   */
  return {
    kind: 'unreachable',
    reason: 'Connaro answered in a way the Console did not recognise.',
  };
}

export function Composer(): React.JSX.Element | null {
  const { can, activeWorkspace } = useSession();
  /*
   * A control the actor cannot use is not rendered at all (§4.2, §10).
   *
   * A VIEWER shown a Send button has been invited to fail in front of an
   * audience: the server refuses `chat.send` with a 403 whatever the browser
   * believes. `can` decides what to draw and never whether a request is
   * allowed — the backend stays authoritative.
   */
  const permitted = can('chat.send');
  /*
   * Switching workspace throws this box away and asks again (§13).
   *
   * The connections on screen belong to ONE workspace. Left in place after a
   * switch they are another tenant's state still visible, and the half-typed
   * message beside them would be aimed at whichever connection happened to
   * still be selected — a message meant for one audience, sent to another.
   */
  const workspaceId = activeWorkspace?.id ?? null;

  const [capabilities, setCapabilities] = useState<ChatSendCapability[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /**
   * The connections whose boxes are ticked.
   *
   * A SET of ids rather than one id: the operator picks any combination, and
   * the request carries exactly what is ticked. Held as ids rather than as
   * capability objects so a reload of `/chat/capabilities` cannot leave the
   * selection pointing at stale rows — a ticked id that no longer resolves to
   * a SUPPORTED connection simply drops out of `chosen`.
   */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [targets, setTargets] = useState<ChatSendConnectionTargets[] | null>(null);
  /*
   * A FAILED channel lookup, kept apart from a lookup still in flight (final
   * review, Minor 10).
   *
   * Both used to collapse into `targets === null`, so a failed `GET
   * /chat/channels` left Send disabled forever behind "Checking where this
   * connection can post…" — a sentence that is false the moment the request
   * has finished — while the real reason sat in a separate alert at the top of
   * the box. §65 wants the reason attached to the control it disables, and
   * that means the two states have to be two states.
   */
  const [targetsError, setTargetsError] = useState<string | null>(null);
  /*
   * The chosen channel PER CONNECTION, not one shared value.
   *
   * With several Discord connections selected at once, a single `channelId`
   * would post to whichever one happened to be rendered last — an operator's
   * message in a server they did not pick. Keyed by connection id so each
   * picker owns its own answer.
   */
  const [channelChoice, setChannelChoice] = useState<Record<string, string>>({});
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // --- what this workspace may send on, and where a message may go ----------

  /*
   * ONE effect, and both requests together.
   *
   * The channel list used to be fetched per selection, which meant a gap
   * between choosing a connection and knowing whether it needed a channel —
   * and with "all platforms" selectable, it would have meant a fan-out of
   * lookups from the browser. `GET /chat/channels` now answers for every
   * connection in the workspace, so there is one answer, fetched once, and
   * selection is a pure client-side narrowing of it.
   */
  useEffect(() => {
    if (!permitted) return;

    const state = { cancelled: false };

    // Nothing from the previous workspace survives the switch.
    setCapabilities(null);
    setTargets(null);
    setTargetsError(null);
    setChannelChoice({});
    setSelected(new Set());
    setText('');
    setOutcome(null);
    setLoadError(null);

    void (async () => {
      try {
        const data = await apiRequest<{ platforms: ChatSendCapability[] }>('/chat/capabilities');
        if (state.cancelled) return;

        setCapabilities(data.platforms);

        /*
         * ONE box ticked to begin with, not all of them.
         *
         * Sending to every audience at once is unrecallable, so it is
         * something an operator does on purpose rather than something they can
         * fail to untick. Every additional platform is one click away and
         * visible the whole time it is armed.
         *
         * The first SUPPORTED connection, which is also what the old
         * single-select picker defaulted to — so nobody's habit changes and no
         * message reaches an audience it would not have reached yesterday.
         */
        const first = data.platforms.find((entry) => entry.state === 'SUPPORTED');
        setSelected(first === undefined ? new Set() : new Set([first.platformConnectionId]));
      } catch (caught) {
        if (state.cancelled) return;
        setCapabilities([]);
        setLoadError(
          caught instanceof ApiError
            ? caught.message
            : 'Connaro could not read which platforms it can send on.',
        );
      }
    })();

    void (async () => {
      try {
        const data = await apiRequest<{ connections: ChatSendConnectionTargets[] }>(
          '/chat/channels',
        );
        if (state.cancelled) return;

        setTargets(data.connections);
        /*
         * A default per connection, so a picker is never rendered with nothing
         * selected. The operator can change any of them; the send carries what
         * is on screen.
         */
        setChannelChoice(
          Object.fromEntries(
            data.connections.flatMap((entry) => {
              const first = entry.channels[0];
              return first === undefined ? [] : [[entry.platformConnectionId, first.id]];
            }),
          ),
        );
      } catch (caught) {
        if (state.cancelled) return;
        setTargets(null);
        /*
         * The server's own words where they exist, and attached to the
         * disabled Send button rather than to the box-wide alert — see
         * `targetsError`'s declaration.
         */
        setTargetsError(
          caught instanceof ApiError
            ? caught.message
            : 'Connaro could not read where these connections may post.',
        );
      }
    })();

    return () => {
      state.cancelled = true;
    };
  }, [permitted, workspaceId]);

  // --- derived --------------------------------------------------------------

  const entries = capabilities ?? [];
  const sendable = useMemo(
    () => entries.filter((entry) => entry.state === 'SUPPORTED'),
    [entries],
  );
  const unavailable = entries.filter((entry) => entry.state !== 'SUPPORTED');

  /**
   * Where this message is actually going.
   *
   * Filtered out of `sendable` rather than mapped out of `selected`, which
   * gives two things: the order is the picker's order (so the over-limit
   * sentence names a stable platform between keystrokes, and the request
   * carries targets in the order they appear on screen), and a ticked id that
   * is no longer SUPPORTED cannot survive into a send.
   */
  const chosen = useMemo(
    () => sendable.filter((entry) => selected.has(entry.platformConnectionId)),
    [selected, sendable],
  );

  const toggle = useCallback((platformConnectionId: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(platformConnectionId)) next.add(platformConnectionId);
      return next;
    });
    setOutcome(null);
  }, []);

  const targetsById = useMemo(
    () => new Map((targets ?? []).map((entry) => [entry.platformConnectionId, entry])),
    [targets],
  );

  /** The chosen connections the SERVER says must name a channel. */
  const needChannel = useMemo(
    () =>
      chosen.flatMap((entry) => {
        const target = targetsById.get(entry.platformConnectionId);
        return target?.requiresChannel === true ? [{ entry, target }] : [];
      }),
    [chosen, targetsById],
  );

  const measured = useMemo(() => measure(text), [text]);
  const overLimit = firstRefusal(chosen, measured);
  const { limit: binding, shortest } = useMemo(() => bindingLimit(chosen), [chosen]);

  /* A required channel with nothing to choose is a reason, not a dead select. */
  const withoutChannels = needChannel.filter(({ target }) => target.channels.length === 0);

  /*
   * The gap between the page opening and knowing where each connection may
   * post. Sending inside it would omit a `channelId` the server may require
   * and earn a 400 the operator did nothing to deserve. Brief, so it is
   * reported as what it is rather than left as a dead button (§65).
   */
  const resolvingTargets = targets === null && targetsError === null;

  /** The channel pickers actually on screen: ticked, required, and answerable. */
  const channelPickers = needChannel.filter(({ target }) => target.channels.length > 0);

  /*
   * Nothing ticked is a REASON, not a dead button (§65). It is also the state
   * an operator reaches deliberately — unticking the last platform — so it has
   * to say what to do rather than just stop working.
   */
  const nothingChosen = chosen.length === 0;

  const blocked =
    nothingChosen || withoutChannels.length > 0 || resolvingTargets || targetsError !== null;

  const handleSubmit = useCallback(
    async (event: React.SyntheticEvent): Promise<void> => {
      event.preventDefault();

      /*
       * The client check is a courtesy and it is treated as one: it stops an
       * obviously doomed request, and the server checks again regardless. A
       * refusal from the server is never treated as impossible — it is
       * rendered, in the server's words.
       */
      if (sending || blocked) return;
      if (text.trim() === '' || overLimit !== null) return;

      setSending(true);
      setOutcome(null);

      const result = await postChatSend({
        text,
        targets: chosen.map((entry) => {
          const target = targetsById.get(entry.platformConnectionId);
          return {
            platformConnectionId: entry.platformConnectionId,
            /*
             * ONLY when the server said a channel is required for THIS
             * connection. Sending one otherwise is a 400 by design, because
             * an ignored field is a request the operator believes was
             * honoured.
             */
            ...(target?.requiresChannel === true
              ? { channelId: channelChoice[entry.platformConnectionId] ?? '' }
              : {}),
          };
        }),
      });

      setSending(false);
      setOutcome(result);

      /*
       * Cleared when EVERY target reported SENT, and not otherwise.
       *
       * On a failure the operator will want to try again without retyping; on
       * an unknown the message may already be on the stream, and emptying the
       * box would present an unfinished story as a finished one. A partial
       * success is the sharpest case of all — some of it went out, and the
       * box has to stay so the operator can decide what to do about the rest.
       */
      if (result.kind === 'results' && result.results.every((entry) => entry.status === 'SENT')) {
        setText('');
      }
    },
    [sending, blocked, text, overLimit, chosen, targetsById, channelChoice],
  );

  if (!permitted) return null;

  return (
    <section className="panel composer" aria-labelledby="composer-heading">
      <div className="panel__header">
        <h2 className="panel__title" id="composer-heading">
          Send a message
        </h2>
      </div>

      <div className="panel__body composer__body">
        {loadError !== null && <Alert tone="warning">{loadError}</Alert>}

        {capabilities === null ? (
          <p className="field__hint">Loading…</p>
        ) : (
          <>
            {sendable.length === 0 ? (
              <p className="field__hint">
                No connected platform can send messages from Connaro right now.{' '}
                <Link to="/connections">Manage connections</Link>
              </p>
            ) : (
              <form className="composer__form" onSubmit={(event) => void handleSubmit(event)}>
                <div className="composer__targets">
                  {/*
                   * A CHECKBOX PER PLATFORM, all of them visible at once.
                   *
                   * A `fieldset`/`legend` rather than a label and a control:
                   * this is one question with several independent answers, and
                   * that is the markup assistive technology reads as such.
                   *
                   * ONLY the platforms that CAN send are here. A ticked box has
                   * to mean "this message is going here", so a box that could
                   * never send would be a control the operator has to learn to
                   * ignore. The ones that cannot are listed underneath with the
                   * adapter's own sentence saying why, which is §65's
                   * requirement — a disabled control with nothing attached is
                   * the bug, and a control that is not there at all with the
                   * reason spelled out beneath it is the fix.
                   */}
                  <fieldset className="composer__platforms">
                    <legend className="field__label">Send to</legend>

                    {sendable.map((entry) => (
                      <label className="composer__platform" key={entry.platformConnectionId}>
                        <input
                          type="checkbox"
                          className="composer__platform-box"
                          checked={selected.has(entry.platformConnectionId)}
                          onChange={() => {
                            toggle(entry.platformConnectionId);
                          }}
                        />
                        {/*
                         * The mark, then the name. Mid-broadcast an operator
                         * picks targets by shape rather than by reading four
                         * words, and the name stays because it is what a
                         * screen reader announces and what somebody who does
                         * not recognise the mark can still use. No `label` on
                         * the icon: the name is right beside it.
                         */}
                        <PlatformIcon
                          platform={entry.platform}
                          displayName={entry.displayName}
                          size={18}
                        />
                        <span className="composer__platform-name">{entry.displayName}</span>
                      </label>
                    ))}
                  </fieldset>

                  {/*
                   * Rendered on the SERVER's answer, never on the platform
                   * name. Discord is the platform this is true of today; the
                   * Console does not need to know that, and must not encode it.
                   *
                   * ONLY FOR TICKED CONNECTIONS. `needChannel` is derived from
                   * `chosen`, so unticking Discord takes its channel picker off
                   * the screen — asking which channel to post in while nothing
                   * is going to post there is a question with no answer, and a
                   * stale one left on screen reads as a message that is still
                   * going to it.
                   *
                   * One picker per connection that needs one, because several
                   * can be ticked at once and a shared value would post to
                   * whichever was rendered last. Named by its own connection
                   * whenever there is more than one to tell apart.
                   */}
                  {channelPickers.map(({ entry, target }) => (
                    <div className="composer__field" key={entry.platformConnectionId}>
                      <label
                        className="field__label"
                        htmlFor={`composer-channel-${entry.platformConnectionId}`}
                      >
                        {channelPickers.length > 1 ? `${entry.displayName} channel` : 'Channel'}
                      </label>
                      <select
                        id={`composer-channel-${entry.platformConnectionId}`}
                        className="field__input"
                        value={channelChoice[entry.platformConnectionId] ?? ''}
                        onChange={(changed) => {
                          const value = changed.target.value;
                          setChannelChoice((current) => ({
                            ...current,
                            [entry.platformConnectionId]: value,
                          }));
                          setOutcome(null);
                        }}
                      >
                        {target.channels.map((channel) => (
                          <option key={channel.id} value={channel.id}>
                            {/* Null where discovery never learned a name; the
                                id is the honest fallback. */}
                            {channel.name ?? channel.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>

                {/*
                 * There is no "goes to Twitch, Kick and Discord" line, and
                 * there was one while a dropdown could say "all platforms".
                 * The ticked boxes are that sentence, and they are already on
                 * screen — restating them underneath would be the same list
                 * twice, one of which could go stale.
                 *
                 * Nothing ticked is the one state the boxes do NOT explain on
                 * their own: an empty set looks the same as not having chosen
                 * yet. So it says what to do, beside the button it disables
                 * (§65).
                 */}
                {nothingChosen && (
                  <p className="composer__reason">
                    Tick at least one platform to send this message to.
                  </p>
                )}

                {/*
                 * NAMED BY THE CONNECTION, never by the platform this rule
                 * happens to be true of today. The branch above is driven by
                 * `requiresChannel`, which is the server's answer for ANY
                 * connection — so a sentence hardcoding "Discord" here would
                 * put the very rule this component refuses to encode back
                 * into the copy, and tell an operator about Discord while
                 * they are looking at something else.
                 */}
                {withoutChannels.map(({ entry }) => (
                  <p className="composer__reason" key={entry.platformConnectionId}>
                    No {entry.displayName} channel is connected for this server, so there is nowhere
                    to post. Choose one on the <Link to="/connections">Connections</Link> page.
                  </p>
                ))}

                {/* A brief wait, said out loud rather than shown as a dead button. */}
                {resolvingTargets && (
                  <p className="composer__reason">Checking where these connections can post…</p>
                )}

                {/*
                 * The lookup FAILED, which is a different thing from the
                 * lookup being slow — and it is permanent until the operator
                 * changes something. The reason goes here, beside the button
                 * it disables (§65), in the server's own words.
                 */}
                {targetsError !== null && <p className="composer__reason">{targetsError}</p>}

                <div className="composer__field">
                  <label className="field__label" htmlFor="composer-text">
                    Message
                  </label>
                  <textarea
                    id="composer-text"
                    className="field__input composer__input"
                    rows={2}
                    value={text}
                    aria-describedby="composer-count"
                    aria-invalid={overLimit !== null}
                    onChange={(changed) => {
                      setText(changed.target.value);
                      setOutcome(null);
                    }}
                  />
                </div>

                <div className="composer__footer">
                  {/*
                   * THE SHORTEST limit among the platforms selected, and both
                   * of Kick's axes where it is one of them. One number would
                   * tell an operator they have 499 characters left while the
                   * byte budget is what is about to refuse them; the longest
                   * platform's number would tell them they have room right up
                   * to the moment the shortest one refuses the whole message.
                   *
                   * `id`, not only a test hook: the textarea's
                   * `aria-describedby` points here, and without it a screen
                   * reader announces neither the count nor the over-limit
                   * state — the two things this element exists to say.
                   */}
                  <span
                    id="composer-count"
                    className={`composer__count${overLimit === null ? '' : ' composer__count--over'}`}
                    data-testid="composer-count"
                  >
                    {binding === null
                      ? `${String(measured.graphemes)} characters`
                      : `${String(measured.graphemes)} / ${String(binding.graphemes)} characters`}
                    {binding?.utf8Bytes === undefined
                      ? ''
                      : ` · ${String(measured.utf8Bytes)} / ${String(binding.utf8Bytes)} bytes`}
                  </span>

                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={sending || text.trim() === '' || blocked}
                  >
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>

                {/*
                 * WHOSE limit that is, when the selected platforms disagree.
                 *
                 * "800 / 500" beside a Discord connection that takes 2000 is
                 * a number with no explanation attached. Said only when the
                 * limits actually differ — on a single platform, or several
                 * that agree, the figure speaks for itself.
                 */}
                {shortest !== null && binding !== null && (
                  <p className="composer__reason">
                    {shortest} has the shortest limit of the platforms selected:{' '}
                    {String(binding.graphemes)} characters.
                  </p>
                )}

                {/*
                 * Said once, live, rather than again on submit: two copies of
                 * the same sentence on screen is two things to read.
                 */}
                {overLimit !== null && <p className="composer__reason">{overLimit}</p>}

                {outcome !== null && <SendOutcome outcome={outcome} />}
              </form>
            )}

            {/*
             * §65, and the whole reason this list exists: every platform that
             * cannot be sent to says why, in the words the SERVER gave —
             * reconnect, re-invite the bot, or the platform publishes no way
             * to do it. A greyed-out option with nothing attached is the bug.
             */}
            {unavailable.length > 0 && (
              <ul className="composer__unavailable">
                {unavailable.map((entry) => {
                  return (
                    <li key={entry.platformConnectionId} className="composer__unavailable-item">
                      <span className="composer__unavailable-name">
                        <PlatformIcon
                          platform={entry.platform}
                          displayName={entry.displayName}
                          size={16}
                        />
                        {entry.displayName}
                      </span>
                      {entry.lead !== null && (
                        <span className="composer__reason">{entry.lead}</span>
                      )}
                      <span className="composer__reason">{entry.note}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/**
 * The words for one target's outcome, in three shapes that must not be
 * mistaken for each other.
 *
 * SENT is one word, because there is nothing else to say. FAILED leads with
 * "Not sent." and then the reason. UNKNOWN says Connaro does not know and
 * tells the operator where to look — because the only safe next step is to
 * check the stream, and the dangerous one is to press Send again.
 */
const UNKNOWN_LEAD = 'Connaro could not confirm this message was sent.';
const UNKNOWN_ADVICE = 'Check your chat before sending it again — it may already be there.';

/**
 * The tone for a whole fan-out: the WORST thing that happened, not the most
 * common.
 *
 * Uncertainty outranks failure, which outranks success — the same precedence
 * the server uses for the HTTP code, and for the same reason. A run of three
 * SENTs and one UNKNOWN is not a success with a footnote; the footnote is the
 * only part that needs an operator to do something.
 */
function groupTone(results: readonly ChatSendTargetResult[]): 'success' | 'warning' | 'error' {
  if (results.some((entry) => entry.status === 'UNKNOWN')) return 'warning';
  if (results.some((entry) => entry.status === 'FAILED')) return 'error';
  return 'success';
}

/**
 * What happened.
 *
 * Every reason is UNTRUSTED text from a provider or from Connaro's own error
 * path, rendered as a React text node in an element of its own (§21, §57).
 */
function SendOutcome({ outcome }: { outcome: Outcome }): React.JSX.Element {
  if (outcome.kind === 'refused') {
    return (
      <Alert tone="error">
        <p className="composer__outcome-lead">Not sent.</p>
        <p className="composer__outcome-detail">{outcome.reason}</p>
      </Alert>
    );
  }

  if (outcome.kind === 'unreachable') {
    return (
      <Alert tone="warning">
        <p className="composer__outcome-lead">{UNKNOWN_LEAD}</p>
        <p className="composer__outcome-detail">{UNKNOWN_ADVICE}</p>
        <p className="composer__outcome-detail">{outcome.reason}</p>
      </Alert>
    );
  }

  const [only] = outcome.results;

  /*
   * ONE target reads as one sentence, not as a list of length one.
   *
   * Naming the platform back to an operator who just picked it is noise, and
   * it is the overwhelmingly common case — the fan-out shape must not make
   * the ordinary send wordier.
   */
  if (outcome.results.length === 1 && only !== undefined) {
    if (only.status === 'SENT') return <Alert tone="success">Sent</Alert>;

    if (only.status === 'UNKNOWN') {
      return (
        <Alert tone="warning">
          <p className="composer__outcome-lead">{UNKNOWN_LEAD}</p>
          <p className="composer__outcome-detail">{UNKNOWN_ADVICE}</p>
          {only.reason !== undefined && (
            <p className="composer__outcome-detail">{only.reason}</p>
          )}
        </Alert>
      );
    }

    return (
      <Alert tone="error">
        <p className="composer__outcome-lead">Not sent.</p>
        {only.reason !== undefined && <p className="composer__outcome-detail">{only.reason}</p>}
      </Alert>
    );
  }

  /*
   * SEVERAL TARGETS, AND EVERY ONE OF THEM SAYS ITS OWN OUTCOME.
   *
   * There is no summary line, deliberately. Any single word for a mixed result
   * — "partly sent", "mostly sent" — is wrong about at least one platform, and
   * an operator working out whether to retype a message needs to know exactly
   * which audiences saw it. The Alert's tone is the worst outcome present, so
   * a single UNKNOWN buried among successes still colours the whole block
   * (§65: colour is the second signal, and the rows below are the first).
   */
  return (
    <Alert tone={groupTone(outcome.results)}>
      <ul className="composer__results">
        {outcome.results.map((result) => (
          <li className="composer__result" key={result.platformConnectionId}>
            <span className="composer__result-name">{result.displayName}</span>
            <span className="composer__outcome-lead">
              {result.status === 'SENT'
                ? 'Sent'
                : result.status === 'UNKNOWN'
                  ? 'Not confirmed'
                  : 'Not sent'}
            </span>
            {result.status === 'UNKNOWN' && (
              <span className="composer__outcome-detail">{UNKNOWN_ADVICE}</span>
            )}
            {result.reason !== undefined && (
              <span className="composer__outcome-detail">{result.reason}</span>
            )}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
